import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL, getAuthHeaders } from './auth';
import AreDisclaimer from './AreDisclaimer';
import './AdvisorVoiceDialog.css';

// Voice-activity-detection settings (ported from the EL-Banking / minthy S2S).
const VAD_THRESHOLD = 0.025;
const MIN_SPEECH_MS = 400;
// Silence/hangover (ms) after MIN_SPEECH before the turn is treated as finished.
// 2000ms tolerates natural thinking pauses (e.g. recalling a company name) instead
// of cutting people off mid-sentence. The no-speech / max-recording caps below are
// kept ON TOP so the mic can never hang open.
const SILENCE_MS = 1700;
const MAX_CONSECUTIVE_FAILURES = 3;
// Hard safety nets so the mic never stays open forever (the rAF VAD loop can miss
// the end of a turn on continuous background noise, and rAF pauses entirely when
// the tab is hidden). These use setTimeout, which keeps firing in a hidden tab.
const NO_SPEECH_TIMEOUT_MS = 10000;  // no speech detected at all → end the turn
const MAX_RECORDING_MS = 30000;      // force-stop & send after this long no matter what

const genVoiceSession = () => `are-voice-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

/**
 * Turn-based speech-to-speech overlay, shared by the two chats in the ARE modal.
 * Listens (VAD) → POST audio → stream transcript/text/audio → play the audio
 * queue → auto-listen.
 *
 * Everything below the first three props defaults to the ARE assistant's
 * behaviour, so that call site needs to pass nothing new. The Not Controlled
 * Chat overrides them to get a second, differently wired voice mode:
 *
 *   endpoint      which backend turn handler the audio is POSTed to
 *   greetingKey   the spoken entry line
 *   searchingKey  status line while the model queries the web (null = no search)
 *   disclaimerKey overrides AreDisclaimer's default wording
 *   sessionId     share ONE thread with a text chat instead of opening a fresh
 *                 voice session — spoken and typed turns then see each other
 *   onTurn        hand the finished turn back so it joins the written transcript
 *   onRunStarted  ARE only: a Run was created, hand off to the Analysen tab
 */
function AdvisorVoiceDialog({
  language = 'de',
  onClose,
  onRunStarted,
  endpoint = '/api/are/assistant/voice',
  greetingKey = 'are_voice_greeting',
  searchingKey = null,
  disclaimerKey,
  sessionId = null,
  onTurn = null,
}) {
  const { t } = useTranslation();
  // Without a sessionId: a fresh session per open, so no old chat history bleeds
  // into a new S2S session (the ARE assistant wants that). With one: the caller's
  // thread, so the spoken turns continue the written conversation.
  const voiceSessionRef = useRef(sessionId || genVoiceSession());
  const [voiceState, setVoiceState] = useState('idle');
  const [statusText, setStatusText] = useState('');
  const [responseText, setResponseText] = useState('');
  const [messages, setMessages] = useState([]);
  const [audioLevel, setAudioLevel] = useState(0);

  const mediaStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const vadFrameIdRef = useRef(null);
  const speechStartTimeRef = useRef(null);
  const silenceStartTimeRef = useRef(null);
  const listenStartTimeRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const voiceStoppedByUserRef = useRef(false);
  const consecutiveFailuresRef = useRef(0);
  const abortControllerRef = useRef(null);

  // TTS playback queue
  const ttsAudioContextRef = useRef(null);
  const currentSourceRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const streamDoneRef = useRef(false);
  const botResponseRef = useRef('');
  const pendingRunRef = useRef(null);   // run_started data awaiting `done`
  const turnQuestionRef = useRef('');   // this turn's transcript, for onTurn
  const turnSourcesRef = useRef([]);    // this turn's url citations, for onTurn
  const startListeningRef = useRef(null);

  const playNextInQueue = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      if (streamDoneRef.current) {
        // A run was started in this turn → hand off after the confirmation finished speaking.
        if (pendingRunRef.current) {
          const data = pendingRunRef.current;
          pendingRunRef.current = null;
          voiceStoppedByUserRef.current = true;
          if (onRunStarted) onRunStarted(data);
          return;
        }
        setVoiceState('idle');
        if (!voiceStoppedByUserRef.current) {
          setTimeout(() => startListeningRef.current && startListeningRef.current(), 300);
        }
      }
      return;
    }

    isPlayingRef.current = true;
    const audioBase64 = audioQueueRef.current.shift();
    try {
      if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
        ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = ttsAudioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const binaryString = atob(audioBase64);
      const arrayBuffer = new ArrayBuffer(binaryString.length);
      const bytes = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);

      ctx.decodeAudioData(arrayBuffer).then((buffer) => {
        if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') return;
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => { currentSourceRef.current = null; playNextInQueue(); };
        currentSourceRef.current = source;
        source.start(0);
        setVoiceState('speaking');
      }).catch((err) => { console.error('Audio decode error:', err); playNextInQueue(); });
    } catch (err) {
      console.error('Playback error:', err);
      playNextInQueue();
    }
  }, [onRunStarted]);

  const startListening = useCallback(() => {
    if (startListeningRef.current) startListeningRef.current();
  }, []);

  const sendVoiceToBackend = useCallback(async (audioBlob) => {
    // Hard-stop the previous turn BEFORE starting a new one: abort any in-flight
    // request and stop the currently-playing audio source. Otherwise clearing the
    // queue + resetting isPlaying (below) leaves the old source playing and a new
    // playback chain starts on top → two overlapping voices, slightly offset.
    if (abortControllerRef.current) { try { abortControllerRef.current.abort(); } catch (e) { /* noop */ } }
    if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch (e) { /* noop */ } currentSourceRef.current = null; }
    setVoiceState('thinking');
    setStatusText(t('are_voice_processing'));
    botResponseRef.current = '';
    streamDoneRef.current = false;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    pendingRunRef.current = null;
    turnQuestionRef.current = '';
    turnSourcesRef.current = [];

    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');
    formData.append('session_id', voiceSessionRef.current);
    formData.append('language', language);

    abortControllerRef.current = new AbortController();
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
        signal: abortControllerRef.current.signal,
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (eventType === 'transcript') {
                turnQuestionRef.current = data.text || '';
                setMessages((prev) => [...prev, { role: 'user', text: data.text }]);
                consecutiveFailuresRef.current = 0;
              } else if (eventType === 'search_status') {
                // Only the chats that actually search pass a key for this; the
                // status line explains what the pause before the answer is.
                if (searchingKey) setStatusText(data.active ? t(searchingKey) : t('are_voice_processing'));
              } else if (eventType === 'sources') {
                turnSourcesRef.current = Array.isArray(data.sources) ? data.sources : [];
              } else if (eventType === 'text') {
                botResponseRef.current += data.chunk;
                setResponseText(botResponseRef.current);
              } else if (eventType === 'audio') {
                audioQueueRef.current.push(data.audio);
                if (!isPlayingRef.current) playNextInQueue();
              } else if (eventType === 'run_started') {
                pendingRunRef.current = data;
              } else if (eventType === 'done') {
                streamDoneRef.current = true;
                if (data.reason === 'empty_transcript') {
                  consecutiveFailuresRef.current += 1;
                  if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                    voiceStoppedByUserRef.current = true;
                    setVoiceState('idle');
                    return;
                  }
                }
                if (botResponseRef.current) {
                  setMessages((prev) => [...prev, { role: 'assistant', text: botResponseRef.current }]);
                  // The spoken turn joins the written conversation, so closing the
                  // overlay leaves the thread complete. The backend already wrote
                  // the same pair to chat_history under this session id.
                  if (onTurn) {
                    onTurn({
                      question: turnQuestionRef.current,
                      answer: botResponseRef.current,
                      sources: turnSourcesRef.current,
                    });
                  }
                }
                setResponseText('');
                if (!isPlayingRef.current) {
                  // Nothing playing → handle handoff / restart immediately.
                  if (pendingRunRef.current) {
                    const d = pendingRunRef.current;
                    pendingRunRef.current = null;
                    voiceStoppedByUserRef.current = true;
                    if (onRunStarted) onRunStarted(d);
                    return;
                  }
                  setVoiceState('idle');
                  if (!voiceStoppedByUserRef.current) {
                    setTimeout(() => startListeningRef.current && startListeningRef.current(), 300);
                  }
                }
              } else if (eventType === 'error') {
                console.error('ARE voice error:', data.message);
                setVoiceState('idle');
              }
            } catch (e) { /* non-JSON data line */ }
            eventType = '';
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('ARE voice backend error:', err);
        setVoiceState('idle');
      }
    }
  }, [language, t, playNextInQueue, onRunStarted, endpoint, searchingKey, onTurn]);

  // Spoken entry greeting (a warm flowing sentence, not the TSLA example).
  const playGreeting = useCallback(async () => {
    const text = t(greetingKey);
    setVoiceState('speaking');
    setStatusText('');
    setResponseText(text);
    setMessages([{ role: 'assistant', text }]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/fetch_speech_from_text?text=${encodeURIComponent(text)}&language=${language}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (!data.speech_file) return;
      if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
        ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = ttsAudioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const binaryString = atob(data.speech_file);
      const arrayBuffer = new ArrayBuffer(binaryString.length);
      const bytes = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
      const buffer = await ctx.decodeAudioData(arrayBuffer);
      if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') return;
      await new Promise((resolve) => {
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => { currentSourceRef.current = null; resolve(); };
        currentSourceRef.current = source;
        source.start(0);
      });
    } catch (err) {
      console.error('Greeting playback error:', err);
    }
    setResponseText('');
  }, [language, t, greetingKey]);

  const initListening = useCallback(async () => {
    setVoiceState('initializing');
    setStatusText(t('are_voice_mic_setup'));
    voiceStoppedByUserRef.current = false;
    consecutiveFailuresRef.current = 0;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      startListeningRef.current = () => {
        if (voiceStoppedByUserRef.current || !mediaStreamRef.current) return;
        setVoiceState('listening');
        setStatusText(t('are_voice_listening'));
        speechStartTimeRef.current = null;
        silenceStartTimeRef.current = null;
        audioChunksRef.current = [];

        const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
        let mimeType = '';
        for (const mt of mimeTypes) { if (MediaRecorder.isTypeSupported(mt)) { mimeType = mt; break; } }

        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        mediaRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        recorder.onstop = () => {
          if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null; }
          if (audioChunksRef.current.length > 0) {
            const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
            sendVoiceToBackend(blob);
          }
        };
        recorder.start(100);
        listenStartTimeRef.current = Date.now();
        // Absolute safety net via setTimeout (still fires in a hidden tab where the
        // rAF VAD loop is paused): force-stop & send so the mic never hangs open.
        if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = setTimeout(() => {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
        }, MAX_RECORDING_MS);

        const dataArray = new Float32Array(analyser.frequencyBinCount);
        const vadLoop = () => {
          if (voiceStoppedByUserRef.current) return;
          analyser.getFloatTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
          const rms = Math.sqrt(sum / dataArray.length);
          setAudioLevel(rms);
          const now = Date.now();
          // No speech detected at all within the window → end the turn, so
          // "Ich höre…" can never hang forever when the user stays silent.
          if (!speechStartTimeRef.current && listenStartTimeRef.current
              && now - listenStartTimeRef.current >= NO_SPEECH_TIMEOUT_MS) {
            if (recorder.state === 'recording') recorder.stop();
            return;
          }
          if (rms > VAD_THRESHOLD) {
            silenceStartTimeRef.current = null;
            if (!speechStartTimeRef.current) speechStartTimeRef.current = now;
          } else if (speechStartTimeRef.current) {
            const speechDuration = now - speechStartTimeRef.current;
            if (speechDuration >= MIN_SPEECH_MS) {
              if (!silenceStartTimeRef.current) {
                silenceStartTimeRef.current = now;
              } else if (now - silenceStartTimeRef.current >= SILENCE_MS) {
                if (recorder.state === 'recording') recorder.stop();
                return;
              }
            }
          }
          vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        };
        vadFrameIdRef.current = requestAnimationFrame(vadLoop);
      };

      // Speak the greeting first, then start listening.
      await playGreeting();
      if (!voiceStoppedByUserRef.current) startListeningRef.current();
    } catch (err) {
      console.error('Microphone error:', err);
      setVoiceState('idle');
      setStatusText(t('are_voice_mic_denied'));
    }
  }, [sendVoiceToBackend, playGreeting, t]);

  const stopVoice = useCallback(() => {
    voiceStoppedByUserRef.current = true;
    if (vadFrameIdRef.current) cancelAnimationFrame(vadFrameIdRef.current);
    if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null; }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach((tr) => tr.stop()); mediaStreamRef.current = null; }
    if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (e) {} audioContextRef.current = null; }
    if (ttsAudioContextRef.current) { try { ttsAudioContextRef.current.close(); } catch (e) {} ttsAudioContextRef.current = null; }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setVoiceState('idle');
  }, []);

  const stopAndSpeakAgain = useCallback(() => {
    if (voiceState !== 'thinking' && voiceState !== 'speaking') return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch (e) {} currentSourceRef.current = null; }
    if (ttsAudioContextRef.current) { try { ttsAudioContextRef.current.close(); } catch (e) {} ttsAudioContextRef.current = null; }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    streamDoneRef.current = false;
    pendingRunRef.current = null;
    startListening();
  }, [voiceState, startListening]);

  const handleClose = useCallback(() => { stopVoice(); if (onClose) onClose(); }, [stopVoice, onClose]);

  useEffect(() => {
    initListening();
    return () => stopVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interactive = voiceState === 'thinking' || voiceState === 'speaking';

  return (
    <div className="av-overlay">
      <IconButton onClick={handleClose} size="small" className="av-close"
        sx={{ position: 'absolute', top: 10, right: 10, color: 'text.secondary', bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
        <CloseIcon />
      </IconButton>

      <div className="av-body" onClick={interactive ? stopAndSpeakAgain : undefined} style={{ cursor: interactive ? 'pointer' : 'default' }}>
        <div className="av-blob-wrap">
          <div className={`av-blob av-${voiceState}`} style={{ transform: `scale(${1 + audioLevel * 0.3})` }}>
            <div className="av-blob-inner" />
            <div className="av-blob-ring r1" />
            <div className="av-blob-ring r2" />
          </div>
        </div>

        <div className="av-status">{statusText}</div>
        {responseText && <div className="av-response">{responseText}</div>}
        {interactive && <div className="av-hint">{t('are_voice_interrupt')}</div>}
      </div>

      {messages.length > 0 && (
        <div className="av-transcript">
          {messages.map((m, i) => (
            <div key={i} className={`av-msg av-msg-${m.role}`}>
              <span className="av-role">{m.role === 'user' ? t('are_voice_you') : t('are_voice_assistant')}</span>
              <span className="av-text">{m.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Persistent AI disclaimer — kept in the normal column flow (not absolute)
         as the last item, so `.av-body` (flex:1) pushes it to the bottom and the
         transcript stacks ABOVE it instead of rendering behind it. */}
      <AreDisclaimer sx={{ flexShrink: 0, justifyContent: 'center', textAlign: 'center', pb: 1 }}
        textKey={disclaimerKey} />
    </div>
  );
}

export default AdvisorVoiceDialog;
